// src/frontend/src/pages/LandingPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
  ZoomIn,
  Close,
  LocationOn,
  Info,
  Lock,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Rating } from '@mui/material';

const LandingPage = () => {
  // US states map file - needed for the map component
  const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

  // State for image modal
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Sample featured GPU data
  const featuredGpu = {
    name: "NVIDIA GeForce RTX 5070 Ti",
    price: 999.99,
    inStock: true,
    image: "https://pisces.bbystatic.com/image2/BestBuy_US/images/products/92457e58-df62-4f97-88bd-afe1a741f8e9.png;maxHeight=1080;maxWidth=900?format=webp",
    sku: "6614743"
  };

  const manufacturers = [
    {
      name: "ASUS",
      image: "/images/asus.jpg"
    },
    {
      name: "GIGABYTE",
      image: "/images/gigabyte.jpg"
    },
    {
      name: "MSI",
      image: "/images/msi.jpg"
    },
    {
      name: "NVIDIA",
      image: "/images/nvidia.jpg"
    },
    {
      name: "PNY",
      image: "/images/pny.jpg"
    }
  ];

  const stateZipCodes = {
    'Alabama': '36104',
    'Arizona': '85001',
    'Arkansas': '72201',
    'California': '95814',
    'Colorado': '80202',
    'Florida': '32301',
    'Texas': '78701'
  };

  const stateCodesMap = {
    'Alabama': 'AL',
    'Arizona': 'AZ',
    'Arkansas': 'AR',
    'California': 'CA',
    'Colorado': 'CO',
    'Florida': 'FL',
    'Texas': 'TX'
  };

  const generateDemoStateData = () => {
    const states = ["Alabama", "Arizona", "Arkansas", "California", "Colorado", "Florida", "Texas"];
    return states.map(state => ({
      stateName: state,
      stateCode: stateCodesMap[state],
      available: state !== "Alaska"
    }));
  };

  const demoStateData = generateDemoStateData();
  const availableStates = demoStateData.filter(state => state.available);

  return (
    <Box sx={{
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "24px"
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          background: "linear-gradient(135deg, #5865F2 0%, #21b6b7 100%)",
          color: "white",
          padding: { xs: 4, md: 6 },
          marginBottom: 6,
          boxShadow: "0 8px 20px rgba(88, 101, 242, 0.2)",
          textAlign: "center"
        }}
      >
        <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
          Never Miss A Graphics Card Again
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ maxWidth: "800px", margin: "0 auto", opacity: 0.9 }}>
          Real-time tracking of GPU availability at Best Buy. Know exactly when your desired graphics card is in stock.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
          <Button
            variant="contained"
            component={Link}
            to="/stocks"
            size="large"
            sx={{
              bgcolor: "white",
              color: "#5865F2",
              px: 4,
              py: 1.5,
              fontWeight: "bold",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.9)",
              }
            }}
          >
            Browse GPUs
          </Button>
          <Button
            variant="contained"
            target="_blank"
            rel="noreferrer"
            href="https://discord.gg/AVJq4zqSUH"
            size="large"
            sx={{
              bgcolor: "#5865F2", // Discord blue color
              color: "white",
              px: 4,
              py: 1.5,
              fontWeight: "bold",
              "&:hover": {
                bgcolor: "#4752C4", // Slightly darker shade for hover
              }
            }}
          >
            Join Discord
          </Button>
        </Stack>
      </Box>

      {/* Notification Comparison Section */}
      <Box sx={{
        position: "relative",
        mt: { xs: 0, md: 4 },
        mb: { xs: 6, md: 8 },
        mx: "auto",
        maxWidth: "1000px",
        px: 2
      }}>
        <Typography
          variant="h5"
          component="h3"
          align="center"
          fontWeight="bold"
          sx={{ mb: 3 }}
        >
          Receive Instant Discord Notifications
        </Typography>

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 8px 20px rgba(88, 101, 242, 0.3)",
                transform: "translateY(-5px)",
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-10px)",
                  boxShadow: "0 12px 28px rgba(88, 101, 242, 0.4)",
                }
              }}
            >
              <Box sx={{
                bgcolor: "#5865F2",
                color: "white",
                p: 1,
                textAlign: "center",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <Box component="span" sx={{ mr: 1 }}>StockMaid Alerts</Box>
                <Chip
                  label="FASTER"
                  size="small"
                  sx={{
                    bgcolor: "white",
                    color: "#5865F2",
                    fontWeight: "bold",
                    fontSize: "0.6rem",
                    height: 20
                  }}
                />
              </Box>
              <Box sx={{ p: 0 }}>
                <img
                  src="/images/stockmaid.jpg"
                  alt="StockMaid notification showing RTX 5090 in stock"
                  style={{ width: "100%", display: "block" }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "column" },
              alignItems: "center",
              justifyContent: "center",
              my: { xs: 2, md: 0 }
            }}>
              <AccessTimeIcon
                sx={{
                  color: "primary.main",
                  fontSize: { xs: 24, md: 32 },
                  mr: { xs: 2, md: 0 },
                  mb: { xs: 0, md: 1 }
                }}
              />
              <Typography variant="body2" fontWeight="bold">VS</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 8px 20px rgba(88, 101, 242, 0.3)",
                transform: "translateY(-5px)",
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-10px)",
                  boxShadow: "0 12px 28px rgba(88, 101, 242, 0.4)",
                }
              }}
            >
              <Box sx={{
                bgcolor: "#666",
                color: "white",
                p: 1,
                textAlign: "center",
                fontWeight: "bold"
              }}>
                Competitor Alerts
              </Box>
              <Box sx={{ p: 0 }}>
                <img
                  src="/images/nowinstock.jpg"
                  alt="Competitor notification showing RTX 5090 in stock"
                  style={{ width: "100%", display: "block" }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Featured GPU */}
      <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
        Featured Graphics Card
      </Typography>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          marginBottom: 6,
          transition: "transform 0.2s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: 3
          }
        }}
      >
        <Grid container>
          {/* Left column - Product image and basic info */}
          <Grid item xs={12} md={4} sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            p: 0
          }}>
            <CardMedia
              component="img"
              image={featuredGpu.image}
              alt={featuredGpu.name}
              sx={{
                width: "100%",
                height: 300,
                objectFit: "contain",
                p: 2
              }}
            />

            {/* Chip moved to absolute position */}
            {featuredGpu.inStock && (
              <Chip
                label="In Stock"
                color="success"
                sx={{
                  position: "absolute",
                  top: 16,
                  left: 16,
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  py: 0.5,
                  borderRadius: "4px",
                  '& .MuiChip-label': { px: 1.5 }
                }}
              />
            )}

            <Box sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" fontWeight="bold" sx={{ mb: 1 }}>
                {featuredGpu.name}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                SKU: {featuredGpu.sku}
              </Typography>

              <Typography variant="h4" color="primary" fontWeight="bold" sx={{ mb: 2 }}>
                ${featuredGpu.price}
              </Typography>

              <Button
                variant="contained"
                component={Link}
                to={`/skus/${featuredGpu.sku}`}
                sx={{
                  fontWeight: "500",
                  textTransform: "uppercase",
                  px: 3
                }}
              >
                VIEW DETAILS
              </Button>
            </Box>
          </Grid>

          {/* Right column - State availability */}
          <Grid item xs={12} md={8} sx={{ p: 0 }}>
            {/* State Availability Header - With Premium Badge */}
            <Box sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #eee",
              position: "relative"
            }}>
              <LocationOn color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" component="h3" fontWeight="bold">
                State Availability
              </Typography>
              <Tooltip title="StockMaid tracks GPU availability in every state">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Premium Badge */}
              <Chip
                label="PREMIUM"
                size="small"
                sx={{
                  ml: "auto",
                  bgcolor: "gold",
                  color: "rgba(0,0,0,0.7)",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                  height: 24
                }}
              />
            </Box>

            {/* Map with Premium Overlay */}
            <Box sx={{
              position: "relative",
              p: 2,
              height: 220,
              borderBottom: "1px solid #eee"
            }}>
              <ComposableMap
                projection="geoAlbersUsa"
                projectionConfig={{
                  scale: 800
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  filter: "blur(1px)" // Slight blur effect to indicate locked content
                }}
              >
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map(geo => {
                      const stateName = geo.properties.name;
                      const stateInfo = demoStateData.find(s => s.stateName === stateName);
                      const available = stateInfo ? stateInfo.available : false;

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={available ? "#4da375" : "#a34d5d"}
                          stroke="#FFFFFF"
                          strokeWidth={0.5}
                          style={{
                            default: { outline: "none" },
                            hover: {
                              fill: available ? "#6FD19A" : "#D16F79",
                              outline: "none"
                            }
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>

              {/* Legend */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 10,
                  right: 10,
                  display: "flex",
                  bgcolor: "rgba(255,255,255,0.8)",
                  borderRadius: 1,
                  p: 1,
                  zIndex: 5
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: "#4da375", borderRadius: 0.5, mr: 1 }} />
                  <Typography variant="caption">Available</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: "#a34d5d", borderRadius: 0.5, mr: 1 }} />
                  <Typography variant="caption">Unavailable</Typography>
                </Box>
              </Box>

              {/* State count indicator */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  position: "absolute",
                  bottom: 10,
                  left: 10,
                  bgcolor: "rgba(255,255,255,0.8)",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  zIndex: 5
                }}
              >
                Available in {availableStates.length} states
              </Typography>

              {/* Premium Overlay - Just Lock Icon & Text */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: "rgba(255,255,255,0.6)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Lock sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    See State Availability
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Store Locations with Premium Tag */}
            <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2, pb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Available Store Locations
              </Typography>

              <Chip
                label="PREMIUM"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: "gold",
                  color: "rgba(0,0,0,0.7)",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                  height: 20
                }}
              />
            </Box>

            {/* Location table header */}
            <Box sx={{
              display: "flex",
              px: 2,
              py: 1,
              borderBottom: "1px solid #eee",
              borderTop: "1px solid #eee",
              bgcolor: "#f9f9f9"
            }}>
              <Typography
                variant="body2"
                fontWeight="500"
                sx={{
                  flex: 1,
                  color: "text.secondary"
                }}
              >
                State
              </Typography>
              <Typography
                variant="body2"
                fontWeight="500"
                sx={{
                  flex: 1,
                  color: "text.secondary"
                }}
              >
                Zip Code
              </Typography>
            </Box>

            {/* Location rows with Premium Overlay */}
            <Box sx={{
              position: "relative",
              height: "200px",
              overflowY: "hidden"
            }}>
              {/* Blurred state list */}
              <Box sx={{
                px: 2,
                filter: "blur(2px)",
                opacity: 0.7
              }}>
                {availableStates.map(state => (
                  <Box
                    key={state.stateName}
                    sx={{
                      display: "flex",
                      py: 1.5,
                      borderBottom: "1px solid #f5f5f5"
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {state.stateName}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {stateZipCodes[state.stateName] || '00000'}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Premium Overlay - Just Lock Icon & Text */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: "rgba(255,255,255,0.7)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Lock sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    See Store Locations
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Demo Video */}
      <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom sx={{ color: "#1e3a5f" }}>
        Watch How It Works in 30 Seconds
      </Typography>
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: '800px',
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 8px 24px rgba(88, 101, 242, 0.25)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 28px rgba(88, 101, 242, 0.35)',
            }
          }}
        >
          <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 aspect ratio */ }}>
            <Box
              component="video"
              controls
              muted
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                backgroundColor: '#000'
              }}
              src="/videos/demo.mp4"
            />
          </Box>
        </Paper>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{
            mt: 3,
            textAlign: 'center',
            maxWidth: '600px'
          }}
        >
          Watch our 30-second demo to see how StockMaid helps you track graphics card availability in real-time and never miss a restock again!
        </Typography>
      </Box>

      {/* Manufacturers with brand image backgrounds */}
      <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom sx={{ color: "#1e3a5f" }}>
        Browse By Manufacturer
      </Typography>
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {manufacturers.map((manufacturer) => (
          <Grid item key={manufacturer.name} xs={12} sm={6} md={4} lg={2.4}>
            <Card
              component={Link}
              to={`/manufacturers/${manufacturer.name}`}
              sx={{
                position: 'relative',
                height: 140,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                },
                textDecoration: 'none'
              }}
            >
              {/* Background Image */}
              <CardMedia
                component="img"
                image={manufacturer.image}
                alt={`${manufacturer.name} brand image`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />

              {/* Dark Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  transition: 'background-color 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                  }
                }}
              />

              {/* Text */}
              <Typography
                variant="h5"
                component="h3"
                sx={{
                  position: 'relative',
                  color: 'white',
                  fontWeight: 'bold',
                  zIndex: 1,
                  textAlign: 'center',
                  px: 2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                {manufacturer.name}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pro Tips Section - Simplified */}
      <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom sx={{ color: "#1e3a5f" }}>
        Get In-Store Pickup Only Items Shipped to You
      </Typography>
      <Paper
        sx={{
          p: 4,
          borderRadius: 4,
          mb: 6,
          background: "linear-gradient(to right, #f0f2f5, #ffffff)"
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="body1" paragraph>
              <strong>Have you found a GPU that's available in another state?</strong> After making your purchase online call the store and ask them to ship it to you instead!
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(88, 101, 242, 0.25)',
                cursor: 'pointer',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 12px 28px rgba(88, 101, 242, 0.35)',
                }
              }}
              onClick={handleOpen}
            >
              <CardMedia
                component="img"
                image="/images/bestbuy-pickup-shipping-guide.jpg"
                alt="Best Buy checkout showing store phone number"
                sx={{
                  width: '100%',
                  minHeight: 200
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  padding: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ZoomIn sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Click to Enlarge</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Image Enlargement Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90vw',
            maxWidth: 1000,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
          }}>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                zIndex: 1,
                bgcolor: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                }
              }}
            >
              <Close />
            </IconButton>
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'auto'
            }}>
              <img
                src="/images/bestbuy-pickup-shipping-guide.jpg"
                alt="Best Buy checkout process showing store location and phone number (enlarged view)"
                style={{
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom sx={{ color: "#1e3a5f" }}>
        Reviews
      </Typography>
      <Box sx={{ mb: 6 }}>
        {/* Testimonial Cards Grid */}
        <Grid container spacing={3}>
          {/* Testimonial 1 */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 28px rgba(88, 101, 242, 0.35)',
                }
              }}
            >
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Matt P.</Typography>
                    <Typography variant="body2" color="text.secondary">Washington, DC</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <CardMedia
                    component="img"
                    image="/images/testimonial.JPG"
                    alt="Graphics card purchased thanks to StockMaid"
                    sx={{ borderRadius: 2, maxHeight: 200, objectFit: 'contain' }}
                  />
                </Box>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <Rating value={5} readOnly sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">Purchased Mar 2025</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 28px rgba(88, 101, 242, 0.35)',
                }
              }}
            >
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Jessica T.</Typography>
                    <Typography variant="body2" color="text.secondary">Austin, TX</Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <Rating value={5} readOnly sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">Purchased Feb 2025</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 28px rgba(88, 101, 242, 0.35)',
                }
              }}
            >
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Ryan K.</Typography>
                    <Typography variant="body2" color="text.secondary">Seattle, WA</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <Rating value={5} readOnly sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">Purchased Jan 2025</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LandingPage;